import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@utils/cn';

/**
 * Card variants using class-variance-authority
 */
const cardVariants = cva(
  'rounded-lg border bg-card text-card-foreground shadow-sm',
  {
    variants: {
      variant: {
        default: '',
        elevated: 'shadow-md',
        outlined: 'border-2',
        ghost: 'border-transparent shadow-none',
      },
      padding: {
        none: '',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
    },
  },
);

/**
 * Card Root Component
 */
export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  /**
   * Make the card interactive (adds hover effects)
   */
  interactive?: boolean;
}

/**
 * Card Component
 *
 * A flexible card container for grouping related content.
 * Supports composition with CardHeader, CardContent, and CardFooter.
 *
 * @example
 * // Basic card
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Patient Information</CardTitle>
 *   </CardHeader>
 *   <CardContent>
 *     <p>Patient details go here</p>
 *   </CardContent>
 * </Card>
 *
 * @example
 * // Interactive card with hover effects
 * <Card interactive>
 *   <CardContent>Clickable card content</CardContent>
 * </Card>
 *
 * @example
 * // Elevated card with custom padding
 * <Card variant="elevated" padding="lg">
 *   <CardHeader>
 *     <CardTitle>Medical History</CardTitle>
 *     <CardDescription>Last updated: 2025-10-09</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     <p>Medical history details</p>
 *   </CardContent>
 *   <CardFooter>
 *     <Button>Edit History</Button>
 *   </CardFooter>
 * </Card>
 */
const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        cardVariants({ variant, padding }),
        interactive &&
          'cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] active:scale-[0.98]',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

/**
 * Card Header Component
 */
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Add border at the bottom of the header
   */
  divider?: boolean;
}

/**
 * CardHeader Component
 *
 * The header section of a card, typically containing a title and optional description.
 *
 * @example
 * <CardHeader>
 *   <CardTitle>Title</CardTitle>
 *   <CardDescription>Description</CardDescription>
 * </CardHeader>
 */
const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, divider, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col space-y-1.5',
        divider && 'border-b pb-4',
        className,
      )}
      {...props}
    />
  ),
);
CardHeader.displayName = 'CardHeader';

/**
 * Card Title Component
 */
export interface CardTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {
  /**
   * Render as a different heading level
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * CardTitle Component
 *
 * The main title within a card header.
 *
 * @example
 * <CardTitle>Patient Details</CardTitle>
 * <CardTitle as="h3">Section Title</CardTitle>
 */
const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Component = 'h3', ...props }, ref) => (
    <Component
      ref={ref}
      className={cn(
        'text-2xl font-semibold leading-none tracking-tight',
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

/**
 * Card Description Component
 */
const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

/**
 * Card Content Component
 */
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Remove default padding
   */
  noPadding?: boolean;
}

/**
 * CardContent Component
 *
 * The main content area of a card.
 *
 * @example
 * <CardContent>
 *   <p>Card content goes here</p>
 * </CardContent>
 *
 * @example
 * // Without padding (useful for tables or full-width content)
 * <CardContent noPadding>
 *   <Table>...</Table>
 * </CardContent>
 */
const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, noPadding, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(!noPadding && 'pt-0', className)}
      {...props}
    />
  ),
);
CardContent.displayName = 'CardContent';

/**
 * Card Footer Component
 */
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Add border at the top of the footer
   */
  divider?: boolean;
  /**
   * Align footer content
   */
  align?: 'left' | 'center' | 'right' | 'between';
}

/**
 * CardFooter Component
 *
 * The footer section of a card, typically containing actions or metadata.
 *
 * @example
 * <CardFooter>
 *   <Button variant="outline">Cancel</Button>
 *   <Button>Save</Button>
 * </CardFooter>
 *
 * @example
 * // Footer with divider and center alignment
 * <CardFooter divider align="center">
 *   <Button>View Details</Button>
 * </CardFooter>
 */
const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, divider, align = 'left', ...props }, ref) => {
    const alignmentClass = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
      between: 'justify-between',
    }[align];

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-2',
          alignmentClass,
          divider && 'border-t pt-4',
          className,
        )}
        {...props}
      />
    );
  },
);
CardFooter.displayName = 'CardFooter';

/**
 * Compact Card Component
 *
 * A pre-configured compact card with smaller padding and text
 */
export interface CompactCardProps extends CardProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
}

/**
 * CompactCard Component
 *
 * A simplified card component with reduced padding for dense layouts.
 *
 * @example
 * <CompactCard
 *   title="Quick Stats"
 *   description="Last 24 hours"
 * >
 *   <p>42 patients</p>
 * </CompactCard>
 */
const CompactCard = React.forwardRef<HTMLDivElement, CompactCardProps>(
  ({ title, description, children, className, ...props }, ref) => (
    <Card ref={ref} padding="sm" className={className} {...props}>
      {(title || description) && (
        <CardHeader className="pb-2">
          {title && <CardTitle className="text-lg">{title}</CardTitle>}
          {description && (
            <CardDescription className="text-xs">{description}</CardDescription>
          )}
        </CardHeader>
      )}
      <CardContent className="text-sm">{children}</CardContent>
    </Card>
  ),
);
CompactCard.displayName = 'CompactCard';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CompactCard,
  cardVariants,
};
